import React, { useState, useEffect } from "react";
import {
    Modal,
    Button as BootstrapButton,
    Form,
    Row,
    Col,
} from "react-bootstrap";
import {
    TextField,
    Box,
    Autocomplete,
    Button,
    Grid,
    Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Spinner from "../../sections/Spinner";

const securityTypes = [
    "permanent",
    "temporary",
    "unarmed",
    "armed",
    "patrol",
];

const periodValues = [
    { value: "none", label: "None" },
    { value: "everyday", label: "Everyday" },
    { value: "once_a_week", label: "Once a Week" },
    { value: "twice_a_week", label: "Twice a Week" },
    { value: "once_a_month", label: "Once a Month" },
    { value: "twice_a_month", label: "Twice a Month" },
];

const ContactFormPopup = ({
    open,
    onClose,
    initialData,
    onSubmit,
    loading,
    readOnly = false,
}) => {
    const [formData, setFormData] = useState({
        name: "",
        businessName: "",
        email: "",
        phoneNo: "",
        visit_date: "",
        location: "",
        typeOfSecurity: "",
        noOfGuardRequired: "",
        timingFrom: "",
        timingTo: "",
        comments: "",
        reminderDate: null,
        reminderDuration: "",
        reminderComment: "",
    });

    useEffect(() => {
        if (initialData) {
            const [from, to] = (initialData.timingForSecurity || " - ").split(" - ");
            setFormData({
                name: initialData.name || "",
                businessName: initialData.businessName || "",
                email: initialData.email || "",
                phoneNo: initialData.phoneNo || "",
                visit_date: initialData.visit_date ? dayjs(initialData.visit_date).format("YYYY-MM-DD") : "",
                location: initialData.location || "",
                typeOfSecurity: initialData.typeOfSecurity || "",
                noOfGuardRequired: initialData.noOfGuardRequired || "",
                timingFrom: from || "",
                timingTo: to || "",
                comments: initialData.comments || "",
                reminderDate: initialData.reminderTime ? dayjs(initialData.reminderTime) : null,
                reminderDuration: initialData.reminderDuration || "",
                reminderComment: initialData.reminderComment || "",
            });
        } else {
            setFormData({
                name: "",
                businessName: "",
                email: "",
                phoneNo: "",
                visit_date: "",
                location: "",
                typeOfSecurity: "",
                noOfGuardRequired: "",
                timingFrom: "",
                timingTo: "",
                comments: "",
                reminderDate: null,
                reminderDuration: "",
                reminderComment: "",
            });
        }
    }, [initialData, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleAutocompleteChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        const dataToSubmit = {
            ...formData,
            timingForSecurity: `${formData.timingFrom} - ${formData.timingTo}`,
            reminderTime: formData.reminderDate ? formData.reminderDate.format("YYYY-MM-DD hh:mm A") : "",
        };
        onSubmit(dataToSubmit);
    };

    return (
        <Modal show={open} onHide={onClose} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {readOnly ? "View Submission" : initialData ? "Edit Submission" : "New Submission"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleFormSubmit}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Name"
                                name="name"
                                size="small"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Business Name"
                                name="businessName"
                                size="small"
                                required
                                value={formData.businessName}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                size="small"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone No."
                                name="phoneNo"
                                size="small"
                                value={formData.phoneNo}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Visit Date"
                                name="visit_date"
                                type="date"
                                size="small"
                                required
                                InputLabelProps={{ shrink: true }}
                                value={formData.visit_date}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Location"
                                name="location"
                                size="small"
                                required
                                value={formData.location}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={securityTypes}
                                value={formData.typeOfSecurity}
                                onChange={(e, newValue) => handleAutocompleteChange("typeOfSecurity", newValue)}
                                disabled={readOnly}
                                renderInput={(params) => (
                                    <TextField {...params} label="Type of Security" size="small" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="No. of Guards Required"
                                name="noOfGuardRequired"
                                type="number"
                                size="small"
                                value={formData.noOfGuardRequired}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Timing From"
                                name="timingFrom"
                                type="time"
                                size="small"
                                required
                                InputLabelProps={{ shrink: true }}
                                value={formData.timingFrom}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Timing To"
                                name="timingTo"
                                type="time"
                                size="small"
                                required
                                InputLabelProps={{ shrink: true }}
                                value={formData.timingTo}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Comments"
                                name="comments"
                                multiline
                                rows={3}
                                value={formData.comments}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                    </Grid>

                    <hr className="my-4" />
                    <Typography variant="h6" sx={{ mb: 2 }}>Reminder Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    label="Reminder Date & Time"
                                    value={formData.reminderDate}
                                    onChange={(newValue) => handleAutocompleteChange("reminderDate", newValue)}
                                    disabled={readOnly}
                                    sx={{ width: '100%' }}
                                    slotProps={{ textField: { size: 'small' } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={periodValues}
                                getOptionLabel={(option) => option.label || (periodValues.find(p => p.value === option)?.label || option)}
                                value={periodValues.find(p => p.value === formData.reminderDuration) || null}
                                onChange={(e, newValue) => handleAutocompleteChange("reminderDuration", newValue?.value || "")}
                                disabled={readOnly}
                                renderInput={(params) => (
                                    <TextField {...params} label="Frequency" size="small" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Reminder Comment"
                                name="reminderComment"
                                multiline
                                rows={2}
                                value={formData.reminderComment}
                                onChange={handleChange}
                                disabled={readOnly}
                            />
                        </Grid>
                    </Grid>

                    {!readOnly && (
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button variant="outlined" onClick={onClose}>Cancel</Button>
                            <Button variant="contained" type="submit" disabled={loading}>
                                {loading ? <Spinner /> : initialData ? "Update" : "Submit"}
                            </Button>
                        </Box>
                    )}
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default ContactFormPopup;
